import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { AuthShell } from '@/components/AuthShell'
import { Alert, Button, Field, Input } from '@/components/ui'
import { errorMessage, login, loginWithOAuth, requestVerification, type OAuthProvider } from '@/lib/pb'

export function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Set when an OAuth consent request sent the user here to sign in: without it
  // they would land on the dashboard and the app waiting on them would hang.
  const returnTo = safeReturnTo(params.get('returnTo'))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [unverified, setUnverified] = useState(false)
  const [resent, setResent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [oauthBusy, setOauthBusy] = useState<OAuthProvider | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setUnverified(false)
    try {
      await login(email, password)
      // AuthProvider picks up the store change and App swaps in the real routes.
      if (returnTo) void navigate(returnTo, { replace: true })
    } catch (err) {
      const message = errorMessage(err)
      setError(message)
      // authRule = "verified = true" makes an unverified account fail exactly
      // like a wrong password, which is a dead end without this hint.
      setUnverified(/failed to authenticate|invalid/i.test(message))
    } finally {
      setBusy(false)
    }
  }

  function onOAuth(provider: OAuthProvider) {
    // Must run from the click handler without awaiting first — Safari blocks
    // the popup otherwise.
    setError('')
    setOauthBusy(provider)
    loginWithOAuth(provider)
      .then(() => {
        if (returnTo) void navigate(returnTo, { replace: true })
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setOauthBusy(null))
  }

  async function resend() {
    try {
      await requestVerification(email)
      setResent(true)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const anyBusy = busy || oauthBusy !== null

  return (
    <AuthShell
      title="Sign in"
      subtitle="Track what expires, and get told in time to act."
      footer={
        <>
          No account? <Link className="text-brand hover:underline" to="/register">Create one</Link>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="space-y-2">
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            loading={oauthBusy === 'google'}
            disabled={anyBusy}
            onClick={() => onOAuth('google')}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            loading={oauthBusy === 'apple'}
            disabled={anyBusy}
            onClick={() => onOAuth('apple')}
          >
            Continue with Apple
          </Button>
        </div>

        <div className="flex items-center gap-3 text-xs text-ink-muted">
          <span className="h-px flex-1 bg-line" aria-hidden="true" />
          or with email
          <span className="h-px flex-1 bg-line" aria-hidden="true" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {unverified && !resent && (
            <p className="text-xs text-ink-muted">
              If you have not confirmed your email yet, sign-in will fail.{' '}
              <button type="button" onClick={resend} className="text-brand hover:underline">
                Resend the verification email
              </button>
              .
            </p>
          )}
          {resent && <Alert tone="success">Verification email sent. Check your inbox.</Alert>}

          <Field label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <Button type="submit" loading={busy} disabled={anyBusy} className="w-full">
            Sign in
          </Button>

          <p className="text-center text-xs text-ink-muted">
            <Link className="hover:underline" to="/reset-password">
              Forgotten your password?
            </Link>
          </p>
        </form>
      </div>
    </AuthShell>
  )
}

/**
 * Accept only a path within this app.
 *
 * `returnTo` comes from the query string, so without this the sign-in page
 * would happily forward a freshly authenticated user to any URL an attacker put
 * in a link. `//evil.example.com` is rejected too: the browser reads it as a
 * scheme-relative URL, not a path.
 */
function safeReturnTo(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null
  return value
}
